import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { CreditCard, Banknote, TrendingUp, ArrowUpRight, Shield, Loader2, CheckCircle2, Clock, ExternalLink, AlertCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export function KaisoPayTab({ authAxios }) {
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [config, setConfig] = useState(null);
  const [connectStatus, setConnectStatus] = useState(null);
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [saving, setSaving] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, statusRes, balRes, txnRes] = await Promise.all([
        authAxiosRef.current.get("/kaisopay/config"),
        authAxiosRef.current.get("/kaisopay/connect-status"),
        authAxiosRef.current.get("/kaisopay/balance"),
        authAxiosRef.current.get("/kaisopay/transactions?limit=10"),
      ]);
      setConfig(cfgRes.data);
      setConnectStatus(statusRes.data);
      setBalance(balRes.data);
      setTransactions(txnRes.data.transactions || []);
    } catch {
      toast.error("Erro ao carregar dados do KaisoPay");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
    // If returning from Stripe onboarding, refresh status
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "true" || params.get("reconnect") === "true") {
      setTimeout(fetchData, 1500);
    }
  }, [fetchData]);

  const handleConnectStripe = async () => {
    setConnecting(true);
    try {
      const returnUrl = window.location.origin + window.location.pathname;
      const res = await authAxios.get(`/kaisopay/connect-onboarding?return_url=${encodeURIComponent(returnUrl)}`);
      window.location.href = res.data.url;
    } catch (e) {
      toast.error("Erro ao iniciar conexao com Stripe: " + (e?.response?.data?.detail || e.message));
      setConnecting(false);
    }
  };

  const handleToggle = async (enabled) => {
    if (enabled && !connectStatus?.onboarding_complete) {
      toast.error("Complete a conexao com o Stripe primeiro para ativar o KaisoPay");
      return;
    }
    setSaving(true);
    try {
      await authAxios.put("/kaisopay/config", { enabled });
      setConfig(prev => ({ ...prev, enabled }));
      toast.success(enabled ? "KaisoPay ativado!" : "KaisoPay desativado");
    } catch { toast.error("Erro ao atualizar"); }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;

  const rate = ((config?.commission_rate || 0.005) * 100).toFixed(1);
  const isConnected = connectStatus?.onboarding_complete;

  return (
    <div className="space-y-6" data-testid="kaisopay-tab">
      {/* Header + Toggle */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-emerald-600" /> KaisoPay
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            O cliente paga online. O Stripe envia automaticamente para a sua conta — como o Uber. Zero trabalho manual.
          </p>
        </div>
        <Switch
          checked={config?.enabled || false}
          onCheckedChange={handleToggle}
          disabled={saving}
          data-testid="kaisopay-toggle"
        />
      </div>

      {/* How it works */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex gap-3">
        <Shield className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
        <div className="text-sm text-emerald-800">
          <p className="font-bold mb-2">Como funciona o KaisoPay (automatico)</p>
          <ol className="space-y-1 text-xs text-emerald-700 list-decimal list-inside">
            <li>Cliente faz o pedido e paga com cartao (Stripe Checkout)</li>
            <li>Stripe processa o pagamento (taxa Stripe: ~1.4% + €0.25)</li>
            <li>Kaiso cobra {rate}% de taxa de plataforma automaticamente</li>
            <li>O restante é transferido diretamente para a sua conta Stripe</li>
            <li>Stripe envia para o seu banco conforme o calendario de pagamentos</li>
          </ol>
        </div>
      </div>

      {/* Stripe Connect Status */}
      <div className="bg-white border rounded-xl p-5">
        <h4 className="font-bold flex items-center gap-2 mb-4">
          <Banknote className="w-4 h-4" /> Conta Stripe (pagamentos automaticos)
        </h4>

        {isConnected ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-emerald-700">Conta Stripe conectada</p>
                <p className="text-xs text-muted-foreground">Pagamentos e transferencias automaticas ativas</p>
                {connectStatus?.stripe_account_id && (
                  <p className="text-[10px] text-gray-400 font-mono">{connectStatus.stripe_account_id}</p>
                )}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData} className="gap-1">
              <RefreshCw className="w-3 h-3" /> Atualizar
            </Button>
          </div>
        ) : connectStatus?.connected ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-amber-700">Onboarding incompleto</p>
                <p className="text-xs text-muted-foreground">Complete o cadastro no Stripe para ativar os pagamentos</p>
              </div>
            </div>
            <Button onClick={handleConnectStripe} disabled={connecting} className="w-full bg-[#635BFF] hover:bg-[#4f49d4] text-white gap-2">
              {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
              Continuar cadastro no Stripe
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Conecte sua conta Stripe para receber pagamentos automaticamente. Sera criada uma conta Express gratuita ligada ao seu banco.
            </p>
            <Button onClick={handleConnectStripe} disabled={connecting} className="w-full bg-[#635BFF] hover:bg-[#4f49d4] text-white gap-2" data-testid="connect-stripe-btn">
              {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
              Conectar com Stripe
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">
              Sera redirecionado para o Stripe. Processo rapido — precisa de dados bancarios.
            </p>
          </div>
        )}
      </div>

      {/* Balance — only when active */}
      {config?.enabled && isConnected && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white border rounded-xl p-4" data-testid="kaisopay-balance">
              <p className="text-xs text-muted-foreground mb-1">Saldo pendente</p>
              <p className="text-xl font-black text-emerald-600">{(balance?.pending_balance || 0).toFixed(2)}</p>
            </div>
            <div className="bg-white border rounded-xl p-4" data-testid="kaisopay-month-revenue">
              <p className="text-xs text-muted-foreground mb-1">Este mes</p>
              <p className="text-xl font-black">{(balance?.this_month?.revenue || 0).toFixed(2)}</p>
              <p className="text-[10px] text-muted-foreground">{balance?.this_month?.count || 0} transacoes</p>
            </div>
            <div className="bg-white border rounded-xl p-4">
              <p className="text-xs text-muted-foreground mb-1">Total processado</p>
              <p className="text-xl font-black">{(balance?.total_revenue || 0).toFixed(2)}</p>
            </div>
            <div className="bg-white border rounded-xl p-4">
              <p className="text-xs text-muted-foreground mb-1">Taxa Kaiso total</p>
              <p className="text-xl font-black text-blue-600">{(balance?.total_commission || 0).toFixed(2)}</p>
            </div>
          </div>

          <div className="bg-white border rounded-xl p-5">
            <h4 className="font-bold flex items-center gap-2 mb-4"><TrendingUp className="w-4 h-4" /> Transacoes recentes</h4>
            {transactions.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Nenhuma transacao ainda.</p>
            ) : (
              <div className="space-y-2">
                {transactions.map(txn => (
                  <div key={txn.id} className="flex items-center justify-between py-2 border-b last:border-0" data-testid={`kaisopay-txn-${txn.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${txn.status === "completed" ? "bg-emerald-100" : "bg-amber-100"}`}>
                        {txn.status === "completed" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Clock className="w-4 h-4 text-amber-600" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium">Pedido #{(txn.order_id || "").slice(0, 8)}</p>
                        <p className="text-[10px] text-muted-foreground">{new Date(txn.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-emerald-600">+{txn.restaurant_amount?.toFixed(2)}</p>
                      <p className="text-[10px] text-muted-foreground">Total: {txn.order_total?.toFixed(2)} | Taxa: {txn.commission_amount?.toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
